// Product analytics through PostHog (TOM-983, cloud ADR 0466).
//
// Off unless GET /v1/config carries a project key, so local dev, tests, CI and
// lanes send nothing. posthog-js is its own chunk, loaded only when a key is set.
//
// What leaves the browser, by Mike's decisions:
// - page views and clicks. A click names the element, never the words on it
//   (mask_all_text), so readings, boat names and Neo's questions stay here;
// - no session replays and no surveys, whatever the PostHog project says;
// - a signed-in owner is identified by account id, with their email. A Try it
//   guest stays anonymous;
// - every URL loses its query string and fragment, which carry reset,
//   invite, activation, reveal and app handoff codes;
// - nothing from the admin console.

import type { CaptureResult, PostHog, PostHogConfig } from 'posthog-js';

/** The admin console's pages, which send nothing. */
const ADMIN_PATH = /^\/admin(?:\/|$)/;

/** PostHog's init options. Exported for the test. */
export function posthogOptions(host: string): Partial<PostHogConfig> {
  return {
    api_host: host,
    // The router moves with pushState, so a page view per history change.
    capture_pageview: 'history_change',
    capture_pageleave: 'if_capture_pageview',
    person_profiles: 'identified_only',
    mask_all_text: true,
    disable_session_recording: true,
    disable_surveys: true,
    before_send: scrubEvent,
  };
}

/**
 * Drops admin console events and strips the query string and fragment from
 * every URL-valued property, in the event and in the person properties it sets.
 * PostHog reads utm_* parameters into their own properties before this runs,
 * so campaign attribution survives.
 */
export function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (event === null) return null;
  if (ADMIN_PATH.test(pathOf(event.properties))) return null;
  scrubUrls(event.properties);
  scrubUrls(event.$set);
  scrubUrls(event.$set_once);
  return event;
}

function pathOf(props: Record<string, unknown> | undefined): string {
  const pathname = props?.['$pathname'];
  if (typeof pathname === 'string') return pathname;
  const url = props?.['$current_url'];
  if (typeof url === 'string') {
    try {
      return new URL(url).pathname;
    } catch {
      return '';
    }
  }
  return '';
}

function scrubUrls(props: Record<string, unknown> | undefined): void {
  if (props === undefined) return;
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
      props[key] = withoutQuery(value);
    }
  }
}

/** `https://x/reset?token=abc#f` → `https://x/reset`. */
export function withoutQuery(url: string): string {
  try {
    const u = new URL(url);
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    return url.split(/[?#]/, 1)[0] ?? '';
  }
}

/** The part of the PostHog client identity needs, so the test can fake it. */
export type IdentityClient = Pick<PostHog, 'identify' | 'reset' | 'get_property'>;

export interface AnalyticsUser {
  id: string;
  email: string;
  /** A Try it guest (TOM-868). */
  demo: boolean;
}

/**
 * Brings PostHog's idea of who is here in line with the session: an owner is
 * identified by account id with their email; signed out, or a Try it guest,
 * starts a fresh anonymous visitor if this browser was identified before.
 * Nothing while the session probe is still out.
 */
export function syncIdentity(
  client: IdentityClient,
  status: 'unknown' | 'authenticated' | 'anonymous',
  user: AnalyticsUser | null
): void {
  if (status === 'unknown') return;
  if (status === 'authenticated' && user !== null && !user.demo) {
    client.identify(user.id, { email: user.email });
    return;
  }
  if (client.get_property('$user_state') === 'identified') client.reset();
}

let starting: Promise<PostHog | null> | null = null;

/**
 * Loads posthog-js and starts it once per page. Resolves null when the chunk
 * fails to load, and a later call tries again.
 */
export function startAnalytics(key: string, host: string): Promise<PostHog | null> {
  starting ??= import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(key, posthogOptions(host));
      return posthog;
    })
    .catch(() => {
      starting = null;
      return null;
    });
  return starting;
}
